import type { OsasConsultData } from '@common/validation/customControls/osas/osasConsultSchemas/types';
import type { ICheckBoxGroupWithTextFieldProps } from '@frontend/components/fields/CheckBoxGroup';
import type { ValidationErrors } from '@frontend/utils';
import { osasConsultOtherRelevantFindingsLabels } from '@common/validation/customControls/osas/osasConsultSchemas/osasConsultReadSchema';
import { CheckBoxGroupWithTextField } from '@frontend/components/fields/CheckBoxGroup';
import type { SleepStudyFindingsData } from './SleepStudyFindingsFormGroup';

type OsasConsultOtherRelevantFindingsFormGroupProps = {
    data: SleepStudyFindingsData;
    errors: ValidationErrors;
    onChange: (data: Partial<SleepStudyFindingsData>, validateKeys?: string | string[] | undefined) => void;
};

export function OsasConsultOtherRelevantFindingsFormGroup(
    props: Readonly<OsasConsultOtherRelevantFindingsFormGroupProps>
) {
    const { data, errors, onChange } = props;
    const otherRelevantFindingsOptions: ICheckBoxGroupWithTextFieldProps['checkboxOptions'] = [
        {
            checked: data.otherRelevantFindings.inefficientSleepHygiene || false,
            label: osasConsultOtherRelevantFindingsLabels.inefficientSleepHygiene,
            onChange: (value: boolean) => handleChangeOtherRelevantFindingsOptions('inefficientSleepHygiene', value),
        },
        {
            checked: data.otherRelevantFindings.frequentMovements || false,
            label: osasConsultOtherRelevantFindingsLabels.frequentMovements,
            onChange: (value: boolean) => handleChangeOtherRelevantFindingsOptions('frequentMovements', value),
        },
        {
            checked: data.otherRelevantFindings.suboptimalSleepEfficiency || false,
            label: osasConsultOtherRelevantFindingsLabels.suboptimalSleepEfficiency,
            onChange: (value: boolean) => handleChangeOtherRelevantFindingsOptions('suboptimalSleepEfficiency', value),
        },
        {
            checked: data.otherRelevantFindings.indicationOfHeartRhythmDisorder || false,
            label: osasConsultOtherRelevantFindingsLabels.indicationOfHeartRhythmDisorder,
            onChange: (value: boolean) =>
                handleChangeOtherRelevantFindingsOptions('indicationOfHeartRhythmDisorder', value),
        },
        {
            label: 'Anders',
            onChange: (value: string | null) => handleChangeOtherRelevantFindingsOptions('other', value),
            value: data.otherRelevantFindings.other,
        },
    ];

    function handleChangeOtherRelevantFindingsOptions<T extends keyof OsasConsultData['otherRelevantFindings']>(
        key: T,
        value: OsasConsultData['otherRelevantFindings'][T]
    ) {
        onChange({ otherRelevantFindings: { ...data.otherRelevantFindings, [key]: value } }, [
            'otherRelevantFindings',
            `otherRelevantFindings.${String(key)}`,
        ]);
    }

    return (
        <CheckBoxGroupWithTextField
            checkboxOptions={otherRelevantFindingsOptions}
            error={errors.get('otherRelevantFindings') ?? errors.get('otherRelevantFindings.other')}
            hasErrors={errors.has('otherRelevantFindings') || errors.has('otherRelevantFindings.other')}
            label="Overige relevante bevindingen"
        />
    );
}
